// ============================================================================
// LIT-CASE — Group Participations section
// ============================================================================
// Shows every case-group (auction, Adalath, OTS, conciliation, settlement
// drive) this case is a member of, with the per-stage state_data captured
// for THIS case in EACH group. Driven by GET /api/v1/CaseGroup/by-case/{id}.
//
// When the Litigation Case Detail modal is opened with focusGroupId, that
// group is auto-expanded; others stay collapsed.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useCaseMemberships } from 'hooks/api/useCaseGroups';
import type { CaseMembership, MemberStateSnapshot, NoticeRecipient } from 'api/types/caseGroup.types';

interface Props {
  caseId: string;
  focusGroupId?: string | null;
}

const fmtDate = (s?: string | null) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-IN'); } catch { return s; } };
const fmtAmount = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

// Stage order — drives the timeline within each group panel.
const AUCTION_STAGES = [
  { code: 'VALUATION',            label: 'Valued'        },
  { code: 'PRE_SALE_NOTICE_SENT', label: 'Pre-Sale Notice' },
  { code: 'NEWSPAPER_PUBLISHED',  label: 'Newspaper'     },
  { code: 'AUCTION_CONDUCTED',    label: 'Conducted'     },
  { code: 'AUCTION_SETTLED',      label: 'Settled'       },
];

const GroupParticipationsSection: React.FC<Props> = ({ caseId, focusGroupId }) => {
  const { data: memberships, loading, error } = useCaseMemberships(caseId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // On first load (or when focusGroupId changes), expand the focused group.
  useEffect(() => {
    if (focusGroupId) setExpanded(new Set([focusGroupId]));
    else if (memberships.length === 1) setExpanded(new Set([memberships[0].groupId]));
  }, [focusGroupId, memberships.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading group participations…</div>;
  }
  if (error) {
    return <div style={{ padding: '1.5rem', color: '#dc2626' }}>Failed to load: {error.message}</div>;
  }
  if (memberships.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        <IconSystem.tag size={32} style={{ opacity: 0.3, marginBottom: '0.6rem' }} />
        <div style={{ fontSize: '0.92rem' }}>This case isn't a member of any event yet.</div>
        <div style={{ fontSize: '0.78rem', marginTop: '0.4rem' }}>When this case is added to an auction, Adalath or other batch event, it'll show up here.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.85rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Group Participations ({memberships.length})</h3>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
          Auction / Adalath / OTS / Conciliation events this case has joined.
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        {memberships.map(g => (
          <GroupPanel key={g.groupId} m={g} open={expanded.has(g.groupId)} onToggle={() => toggle(g.groupId)} />
        ))}
      </div>
    </div>
  );
};

const GroupPanel: React.FC<{ m: CaseMembership; open: boolean; onToggle: () => void }> = ({ m, open, onToggle }) => {
  const presaleData = m.stateData?.PRE_SALE_NOTICE_SENT;
  const recipients = useMemo<NoticeRecipient[]>(() => {
    if (!presaleData?.dataJson) return [];
    try {
      const parsed = JSON.parse(presaleData.dataJson);
      return Array.isArray(parsed?.recipients) ? parsed.recipients : [];
    } catch { return []; }
  }, [presaleData?.dataJson]);

  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
      {/* Header — always visible */}
      <button type="button" onClick={onToggle}
        style={{
          width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
          padding: '0.65rem 0.9rem', background: 'var(--bg-secondary)',
          display: 'flex', alignItems: 'center', gap: '0.6rem',
        }}>
        <IconSystem.chevronRight size={14} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--text-muted)' }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', fontWeight: 600 }}>
            <span style={{ color: 'var(--text-primary)' }}>{m.groupCode}</span>
            <span style={{ fontSize: '0.7rem', fontWeight: 500, color: '#2563eb', background: 'rgba(37,99,235,0.12)', padding: '1px 7px', borderRadius: 999 }}>
              {m.groupType.replace('_', ' ')}{m.groupSubtype ? ` · ${m.groupSubtype}` : ''}
            </span>
            {m.currentStateCode && (
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Group: {m.currentStateCode}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
            {m.title ? `${m.title} · ` : ''}{fmtDate(m.eventDate)}{m.venue ? ` · ${m.venue}` : ''}{m.seqNo != null ? ` · Lot ${m.seqNo}` : ''}
          </div>
        </div>
        <span style={{
          fontSize: '0.7rem', fontWeight: 600, padding: '1px 8px', borderRadius: 999,
          color: m.participationStatus === 'WITHDRAWN' ? '#dc2626' : m.participationStatus === 'DEFERRED' ? '#d97706' : '#16a34a',
          border: '1px solid currentColor',
        }}>{m.participationStatus}</span>
      </button>

      {open && (
        <div style={{ padding: '0.85rem 1rem', borderTop: '1px solid var(--border-color)' }}>
          {/* Per-stage strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.65rem', marginBottom: '0.85rem' }}>
            {AUCTION_STAGES.map(s => (
              <StageCell key={s.code} stage={s} snap={m.stateData[s.code]} />
            ))}
          </div>

          {/* Recipients (from PRE_SALE_NOTICE_SENT state_data) */}
          {recipients.length > 0 && (
            <RecipientsTable recipients={recipients} />
          )}
        </div>
      )}
    </div>
  );
};

const StageCell: React.FC<{ stage: { code: string; label: string }; snap?: MemberStateSnapshot }> = ({ stage, snap }) => {
  if (!snap) {
    return (
      <div style={{ padding: '0.5rem 0.65rem', border: '1px dashed var(--form-border-color)', borderRadius: 6, opacity: 0.6 }}>
        <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700 }}>{stage.label}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>—</div>
      </div>
    );
  }
  const ok = snap.statusValue === 'SOLD' || snap.statusValue === 'DELIVERED' || snap.statusValue === 'POSTED' || snap.statusValue === 'PUBLISHED' || snap.statusValue === 'DISPATCHED';
  const warn = snap.statusValue === 'UNSOLD' || snap.statusValue === 'RETURNED' || snap.statusValue === 'REFUSED';
  const color = warn ? '#d97706' : ok ? '#16a34a' : '#2563eb';
  return (
    <div style={{ padding: '0.5rem 0.65rem', border: '1px solid var(--border-color)', borderRadius: 6 }}>
      <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700 }}>{stage.label}</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600, color, marginTop: '0.15rem' }}>
        {snap.statusValue || '✓'}
        {snap.amountPrimary != null && <span style={{ marginLeft: '0.35rem', color: 'var(--text-primary)' }}>{fmtAmount(snap.amountPrimary)}</span>}
      </div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
        {snap.eventDate && fmtDate(snap.eventDate)}
        {snap.counterpartyName && <span> · {snap.counterpartyName}</span>}
      </div>
    </div>
  );
};

const RecipientsTable: React.FC<{ recipients: NoticeRecipient[] }> = ({ recipients }) => {
  const statusColor = (s?: string) => {
    if (s === 'DELIVERED') return '#16a34a';
    if (s === 'RETURNED' || s === 'REFUSED' || s === 'NOT_TRACEABLE') return '#dc2626';
    if (s === 'DISPATCHED') return '#2563eb';
    return 'var(--text-muted)';
  };
  return (
    <div>
      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.35rem' }}>
        Pre-sale notice recipients
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 4 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              {['Role', 'Name', 'Mode', 'Dispatched', 'Tracking', 'Status', 'Delivered/Returned'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '0.3rem 0.5rem', fontSize: '0.7rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recipients.map((r, i) => (
              <tr key={i}>
                <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.recipient_role || '—'}</td>
                <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.recipient_name || '—'}</td>
                <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{(r.mode_of_dispatch || '—').replace(/_/g, ' ')}</td>
                <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.dispatch_date ? fmtDate(r.dispatch_date) : '—'}</td>
                <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>{r.tracking_no || '—'}</td>
                <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)', color: statusColor(r.delivery_status), fontWeight: 600 }}>{r.delivery_status || '—'}</td>
                <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                  {r.delivery_date ? fmtDate(r.delivery_date) : '—'}
                  {r.return_reason && <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}> · {r.return_reason}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '0.3rem', fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
        Inline editing of delivery status arrives in the next iteration.
      </div>
    </div>
  );
};

export default GroupParticipationsSection;
